import { supabase } from '../../lib/supabase'
import type { DayEntry } from '../../components/agent/scheduleDay'
import type { PeriodDateSkeleton } from '../../lib/payPeriods'
import { SCHEDULE_ENTRY_COLUMNS, dayFor, type EntryRow } from './approvedScheduleStore'

// One planning-board row: an employee on the team, and their day-by-day
// schedule for one pay period as muster.schedule_entries holds it. An
// employee with no entries in the period has not submitted it (a returned
// or denied submission deletes its own entries, 20260922000000_schedule_submissions.sql),
// so days is empty and editable false, and the board reads Unsubmitted
// across the row. id is the row's identity (a display name is not unique);
// teamDates holds the ISO dates in either period the employee is placed on
// this team, so a mid-period transfer leaves each team's row editable only
// for its own side of the transfer.
export type BoardRosterRow = { id: string; name: string; days: DayEntry[]; editable: boolean; teamDates: Set<string> }
export type BoardRoster = { current: BoardRosterRow[]; next: BoardRosterRow[] }

const EMPTY_ROSTER: BoardRoster = { current: [], next: [] }

type AssignmentRow = {
  employee_id: string
  effective_from: string
  effective_to: string | null
  employees: { display_name: string } | null
}

// A pay period's days for one employee: every date the period covers, an
// entry where the employee has one and an unscheduled day where they do
// not, so a partly submitted period never leaves a hole in the row.
function daysFor(dates: PeriodDateSkeleton[], entries: Map<string, EntryRow>): DayEntry[] {
  return dates.map((date) => {
    const entry = entries.get(date.date)
    const day = entry ? dayFor(entry) : null
    return day ? { ...date, ...day } : { ...date, category: 'unscheduled', label: 'Unscheduled' }
  })
}

/**
 * The signed-in supervisor's team as the planning board's rows: everyone
 * placed on the team (employee_assignments) at any point in either pay
 * period, with each period's real schedule (schedule_entries). Row-level
 * security already limits both tables to the caller's own teams and to
 * an administrator, so the team filter here selects one of them rather
 * than enforcing access.
 */
export async function getBoardRoster(
  teamId: string,
  currentDates: PeriodDateSkeleton[],
  nextDates: PeriodDateSkeleton[],
): Promise<BoardRoster> {
  const firstDate = currentDates[0]?.date
  const lastDate = nextDates[nextDates.length - 1]?.date
  if (!firstDate || !lastDate) return EMPTY_ROSTER

  const { data: assignments, error: assignmentsError } = await supabase
    .from('employee_assignments')
    .select('employee_id, effective_from, effective_to, employees(display_name)')
    .eq('team_id', teamId)
    .eq('active', true)
    .lte('effective_from', lastDate)
    .or(`effective_to.is.null,effective_to.gte.${firstDate}`)
  if (assignmentsError || !assignments) return EMPTY_ROSTER

  const members = new Map<string, string>()
  const placements = new Map<string, AssignmentRow[]>()
  for (const row of assignments as unknown as AssignmentRow[]) {
    if (!row.employees?.display_name) continue
    members.set(row.employee_id, row.employees.display_name)
    placements.set(row.employee_id, [...(placements.get(row.employee_id) ?? []), row])
  }
  if (members.size === 0) return EMPTY_ROSTER

  const { data: entries, error: entriesError } = await supabase
    .from('schedule_entries')
    .select(SCHEDULE_ENTRY_COLUMNS)
    .in('employee_id', Array.from(members.keys()))
    .gte('date', firstDate)
    .lte('date', lastDate)
  if (entriesError || !entries) return EMPTY_ROSTER

  const entriesByEmployee = new Map<string, Map<string, EntryRow>>()
  for (const row of entries as unknown as EntryRow[]) {
    const byDate = entriesByEmployee.get(row.employee_id) ?? new Map<string, EntryRow>()
    byDate.set(row.date, row)
    entriesByEmployee.set(row.employee_id, byDate)
  }

  const names = Array.from(members.entries()).sort((a, b) => a[1].localeCompare(b[1]))

  function rowsFor(dates: PeriodDateSkeleton[]): BoardRosterRow[] {
    const start = dates[0].date
    const end = dates[dates.length - 1].date
    return names.map(([employeeId, name]) => {
      const inPeriod = new Map<string, EntryRow>()
      for (const [date, entry] of entriesByEmployee.get(employeeId) ?? []) {
        if (date >= start && date <= end) inPeriod.set(date, entry)
      }
      const submitted = inPeriod.size > 0
      const teamDates = new Set<string>()
      for (const date of [...currentDates, ...nextDates]) {
        const onTeam = (placements.get(employeeId) ?? []).some(
          (placement) => placement.effective_from <= date.date && (placement.effective_to === null || placement.effective_to >= date.date),
        )
        if (onTeam) teamDates.add(date.date)
      }
      return { id: employeeId, name, days: submitted ? daysFor(dates, inPeriod) : [], editable: submitted, teamDates }
    })
  }

  return { current: rowsFor(currentDates), next: rowsFor(nextDates) }
}
